/* eslint-disable indent */
// This is a simplified builder to manage the Assay Creation config.
import formatRFC3339 from 'date-fns/formatRFC3339';
import fromUnixTime from 'date-fns/fromUnixTime';
import setHours from 'date-fns/setHours';
import startOfHour from 'date-fns/startOfHour';
import { Baseline } from '../../types/Assays/Baseline';
import { Summarizer } from '../../types/Assays/Summarizer';
import { Window } from '../../types/Assays/Window';

type SettingsType = {
  threshold: string,
  aggType: string,
  numBins: number,
  binMode: string,
  binWeights: number[],
  metric: string,
  edges: number[] | null,
  next_run_time: number,
  run_freq: number,
  grouping_freq: number
}

export enum MonitorType {
  OUTPUT,
  INPUT,
}

// Overwrite the config values if specified, or ignore them if omitted (do not use `undefined` directly)
interface ApplyMonitorArgs {
  monitored_type: MonitorType | null | undefined

  monitored_field: string | null | undefined

  monitored_column: number | null | undefined
}

export class AssayConfig {
  name: string | null = null;

  pipeline_id = 0;

  pipeline_name: string | null = null;

  active = true;

  status = 'created';

  get iopath() {
    return this.getIOPath()
  }

  // iopath is really 3 separate properties
  monitored_type = MonitorType.OUTPUT

  monitored_field: string | null = null

  monitored_column: string | null = null

  baseline: Baseline = {
    'Fixed': {
      'pipeline': null,
      'model': null,
      'start_at': null,
      'end_at': null,
    }
  };

  window: Window = {
    'pipeline': null,
    'model': null,
    'width': '24 hours',
    'start': null,
    'end': null,
    'interval': null
  };

  summarizer: Summarizer = {
    'type': 'UnivariateContinuous',
    'bin_mode': 'Quantile',
    'aggregation': 'Density',
    'metric': 'PSI',
    'num_bins': 5,
    'bin_weights': null,
    'bin_width': null,
    'provided_edges': null,
    'alert_threshold': null,
    'add_outlier_edges': true
  };

  warning_threshold = null;

  alert_threshold = 0.1;

  run_until = null;

  workspace_id = 0;

  constructor(oldAssayConfig = {}, overrides = {}) {
    Object.assign(this, oldAssayConfig);
    Object.assign(this, overrides);
  }

  getModel() {
    return this.window.model;
  }

  setModel(model_name: string) {
    this.baseline.Fixed.model = model_name;
    this.window.model = model_name;
    return this;
  }

  setPipeline(pipeline_id: number, pipeline_name: string) {
    this.pipeline_id = pipeline_id;
    this.pipeline_name = pipeline_name;
    this.baseline.Fixed.pipeline = pipeline_name;
    this.window.pipeline = pipeline_name;
    return this;
  }

  setBaselineRange(start: string, end: string) {
    this.baseline.Fixed.start_at = start;
    this.baseline.Fixed.end_at = end;
    return this;
  }

  setWindowRange(start: string, end: string) {
    this.window.start = start;
    this.window.end = end;
    return this;
  }

  setSummarizerOptions(binNum: number, binWeights: number[], alertThreshold: number, metric: string) {
    this.summarizer.metric = metric;
    this.summarizer.num_bins = binNum;
    this.summarizer.bin_weights = binWeights;
    this.summarizer.alert_threshold = alertThreshold;
    return this;
  }

  // NOTE: In the future, this will likely require an input/output enum and 2 ints.
  // setMonitor(str: string) {
  //   this.iopath = str;
  //   return this;
  // }

  applyMonitor(args: ApplyMonitorArgs) {
    return Object.assign(this, args);
  }

  getIOPath() {
    if (!this.monitored_field || !this.monitored_column) {
      // Intentional warning
      // eslint-disable-next-line no-console
      console.warn('IO path was requested without field or column specified!', this.monitored_field, this.monitored_column);
      return '';
    }

    const INFERENCE_DATA_KEYS_REGEX = /^((in)|(out))\.(?<name>.*)/;
    const type = this.monitored_type === MonitorType.OUTPUT ? 'output' : 'input';

    const field = this.monitored_field.match(INFERENCE_DATA_KEYS_REGEX)?.groups?.name;
    if (!field) {
      // This may be legacy JSON format.
      return `${type} ${this.monitored_field} ${this.monitored_column}`;
    }

    return `${type} ${field} ${this.monitored_column}`;
  }

  setName(name: string) {
    this.name = name;
    return this;
  }

  setWorkspace(workspace_id: number) {
    this.workspace_id = workspace_id;
    return this;
  }

  setAdvancedSettings(settings: SettingsType, start: number) {
    this.alert_threshold = parseFloat(settings.threshold);
    this.summarizer.aggregation = settings.aggType
    this.summarizer.num_bins = settings.numBins;
    this.summarizer.bin_mode = settings.binMode;
    this.summarizer.bin_weights = settings.binWeights;
    this.summarizer.metric = settings.metric;
    this.summarizer.provided_edges = settings.edges;

    const specifiedStartDate = fromUnixTime(start);
    const scheduledStartTime = startOfHour(setHours(specifiedStartDate, settings.next_run_time))
    const formattedStartDate = formatRFC3339(scheduledStartTime);

    this.window.start = formattedStartDate;
    this.window.interval = `1 ${settings.run_freq}`;
    this.window.width = `1 ${settings.grouping_freq}`

    return this;
  }
};
